import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { TeamName } from './ScheduleWeeksView';

const formatIdeal = (value) => {
  if (value === null || value === undefined) return '–';
  return Number.isInteger(value) ? String(value) : Number(value).toFixed(1);
};

export const evaluateSlotStat = (stat, slot) => {
  const offIdeal = stat ? Math.abs((stat.games ?? 0) - (stat.ideal ?? 0)) > 1 : false;
  const longStreak = stat && slot ? (stat.longestStreak ?? 0) > (slot.maxConsecutive ?? Infinity) : false;
  return { offIdeal, longStreak };
};

const SlotCellContent = ({ stat, slot }) => {
  const { offIdeal, longStreak } = evaluateSlotStat(stat, slot);
  if (!stat) return <Typography variant="body2" color="text.secondary">–</Typography>;
  return (
    <Box>
      <Tooltip title={offIdeal ? 'More than one game away from the ideal share' : ''}>
        <Typography variant="body2" sx={{ fontWeight: offIdeal ? 700 : 400, color: offIdeal ? 'warning.dark' : 'inherit' }}>
          {`${stat.games} (${formatIdeal(stat.ideal)})`}
        </Typography>
      </Tooltip>
      <Tooltip title={longStreak ? `Longer than the limit of ${slot?.maxConsecutive} weeks in a row` : ''}>
        <Typography variant="caption" sx={{ color: longStreak ? 'error.main' : 'text.secondary', fontWeight: longStreak ? 700 : 400 }}>
          {`streak ${stat.longestStreak}`}
        </Typography>
      </Tooltip>
    </Box>
  );
};

const ScheduleTeamStats = ({ result, teams = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const teamsById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);
  const timeSlots = result?.timeSlots || [];
  const teamStats = result?.teamStats || [];

  const cellBg = (stat, slot) => {
    const { offIdeal, longStreak } = evaluateSlotStat(stat, slot);
    if (longStreak) return alpha(theme.palette.error.main, 0.1);
    if (offIdeal) return alpha(theme.palette.warning.main, 0.12);
    return undefined;
  };

  if (teamStats.length === 0) {
    return <Typography color="text.secondary">No team statistics available.</Typography>;
  }

  const slotLegend = (
    <Typography variant="body2" color="text.secondary">
      Time slot columns show games played (ideal share) and the longest run of consecutive weeks in that slot.
      {timeSlots.length > 0 && ` Limits: ${timeSlots.map((s) => `${s.label} ${s.maxConsecutive} in a row`).join(', ')}.`}
    </Typography>
  );

  if (isMobile) {
    return (
      <Stack spacing={2}>
        {slotLegend}
        {teamStats.map((team) => (
          <Paper key={team.teamId} variant="outlined" sx={{ p: 2 }} data-testid="team-stats-card">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
              <TeamName teamId={team.teamId} name={team.teamName} teamsById={teamsById} />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {`${team.games} games · ${team.home} home · ${team.away} away · ${team.byes} byes`}
            </Typography>
            <Stack spacing={1}>
              {timeSlots.map((slot) => {
                const stat = team.timeSlots?.find((s) => s.key === slot.key);
                return (
                  <Stack
                    key={slot.key}
                    direction="row"
                    spacing={2}
                    sx={{ justifyContent: 'space-between', p: 0.5, borderRadius: 1, bgcolor: cellBg(stat, slot) }}
                  >
                    <Typography variant="body2">{slot.label}</Typography>
                    <SlotCellContent stat={stat} slot={slot} />
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {slotLegend}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Team</TableCell>
              <TableCell align="right">Games</TableCell>
              <TableCell align="right">Home</TableCell>
              <TableCell align="right">Away</TableCell>
              <TableCell align="right">Byes</TableCell>
              {timeSlots.map((slot) => (
                <TableCell key={slot.key} align="center">
                  <Box>{slot.label}</Box>
                  <Typography variant="caption" color="text.secondary">
                    {`max ${slot.maxConsecutive} in a row`}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {teamStats.map((team) => (
              <TableRow key={team.teamId}>
                <TableCell sx={{ wordBreak: 'break-word' }}>
                  <TeamName teamId={team.teamId} name={team.teamName} teamsById={teamsById} />
                </TableCell>
                <TableCell align="right">{team.games}</TableCell>
                <TableCell align="right">{team.home}</TableCell>
                <TableCell align="right">{team.away}</TableCell>
                <TableCell align="right">{team.byes}</TableCell>
                {timeSlots.map((slot) => {
                  const stat = team.timeSlots?.find((s) => s.key === slot.key);
                  return (
                    <TableCell key={slot.key} align="center" sx={{ bgcolor: cellBg(stat, slot) }}>
                      <SlotCellContent stat={stat} slot={slot} />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default ScheduleTeamStats;
